/**
 * SettingsCard — the ONE section wrapper used by every Settings sub-tab.
 *
 * Header strip (bg-paper-soft + border-b) with:
 *   - required title
 *   - optional description
 *   - optional right-side slot (typically a <Badge/>)
 *
 * Deliberately icon-free: titles carry no decorative icon. Icons live in the
 * nav rails, where they aid scanning; in a card header they were noise.
 *
 * Body (space-y-4) for the actual controls, plus an optional footer strip
 * (border-t) with right-aligned actions. If you find yourself reaching for
 * a raw <section className="rounded-surface border..."> in a Settings tab,
 * you should be using this instead.
 */
import type { ReactNode } from 'react';

import { cn } from '../../lib/cn';

export interface SettingsCardProps {
  title: string;
  description?: ReactNode;
  badge?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}

export function SettingsCard({
  title,
  description,
  badge,
  footer,
  children,
  className,
  bodyClassName,
}: SettingsCardProps): JSX.Element {
  return (
    <section
      className={cn(
        'overflow-hidden rounded-surface border border-rule bg-paper',
        className,
      )}
    >
      <header className="flex items-start justify-between gap-4 border-b border-rule bg-paper-soft px-5 py-3.5">
        <div className="min-w-0">
          <h2 className="text-sm font-medium text-ink">{title}</h2>
          {description ? (
            <p className="mt-0.5 text-xs text-ink-muted [text-wrap:pretty]">{description}</p>
          ) : null}
        </div>
        {badge ? <div className="shrink-0">{badge}</div> : null}
      </header>
      <div className={cn('space-y-4 px-5 py-5', bodyClassName)}>{children}</div>
      {footer ? (
        <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-rule bg-paper px-5 py-3.5">
          {footer}
        </footer>
      ) : null}
    </section>
  );
}
